import { useCallback, useEffect, useMemo, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { type BillingInterval } from '@sine/sine-core-api-types';

import type {
  Currency,
  HydratedBillableSite,
  LineItem,
  PlanSelectOption,
} from '../../../services/billing/billing.types';
import { useBilling } from '../BillingProvider';
import {
  formatPlanOptionLabel,
  getChangedLineItems,
  hasAnyDowngrade,
  hasAnyUpgrade,
  isTrial,
  updateNumberOfExistingPlanOptions,
} from '../billing.helpers';

interface UseLineItemManagementParams {
  initialItems?: Array<LineItem>;
  defaultPlanOption?: PlanSelectOption;
  currency: Currency;
  billingCycle?: BillingInterval;
  planOptions?: Array<PlanSelectOption>;
  existingOptions?: Array<PlanSelectOption>;
  setExistingOptions?: (options: Array<PlanSelectOption>) => void;
}

export function useLineItemManagement({
  initialItems = [],
  defaultPlanOption,
  currency,
  billingCycle,
  planOptions,
  existingOptions,
  setExistingOptions,
}: UseLineItemManagementParams) {
  const [lineItems, setLineItems] = useState<Array<LineItem>>(initialItems);
  const [changedLineItems, setChangedLineItems] = useState<Array<LineItem>>(lineItems.filter((item) => item.isNew));

  const { dateOptions } = useBilling();

  // Sync lineItems with initialItems when initialItems changes
  // TODO: REMOVE
  useEffect(() => {
    setLineItems(initialItems);
    setChangedLineItems(initialItems.filter((item) => item.isNew));
  }, [initialItems]);

  const trackChangedLineItem = useCallback(
    (updatedLineItem: LineItem) => {
      setChangedLineItems((prev) => {
        const isNewLineItem = updatedLineItem.isNew;
        const matchingInitialItem = initialItems.find((item) => item.id === updatedLineItem.id);
        const hasSelectionChanged =
          updatedLineItem.selectedPlanOption?.value !== matchingInitialItem?.selectedPlanOption?.value;

        /** Ensure we only keep one entry for the line item, remove any previous entry that matches updatedLineItem:
            The way to determine "previous entry that matches updatedLineItem" is different depending on the the scenarios:
              1. New licence with assigned site, licence id should be undefined and site id used for matching
              2. Existing licence with assigned site, licence id and site id used for matching
              3. Existing licence but it doesn't have site, site id should be undefined and licence id used for matching

          TODO: Ideally we would have a unique identifier for each lineitem to make this easier. 
          To resolve it properly I need to rename the lineItem.id to lineItem.licenceId and set the lineItem.id as unique id for tracking.
          I prefer to do this separately to keep the changes isolated.
        */
        const deduped = prev.filter((item) => {
          const isSameLicence = item.id === updatedLineItem.id;
          const isSameSite = item.site?.id === updatedLineItem.site?.id;
          const isSiteAssigned = !!item.site?.id;
          const isSavedLicence = !!item.id;

          return isSavedLicence ? !isSameLicence && (!isSiteAssigned || !isSameSite) : !isSameSite;
        });

        // Remove the line item from the changed collection if it matches the initial state
        if (!isNewLineItem && !hasSelectionChanged) {
          return deduped;
        }

        return [...deduped, updatedLineItem];
      });
    },
    [initialItems],
  );

  const addLineItem = useCallback(() => {
    const newLineItem: LineItem = {
      site: {
        id: uuidv4(),
        isNew: true,
      } as HydratedBillableSite,
      selectedPlanOption: defaultPlanOption ? formatPlanOptionLabel(defaultPlanOption, currency) : undefined,
      renewalDate: 'N/A',
      licenceType: 'N/A',
    };

    setLineItems((prev) => [...prev, newLineItem]);
    trackChangedLineItem(newLineItem);
  }, [defaultPlanOption, currency, trackChangedLineItem]);

  const removeLineItem = useCallback(
    (lineItem: LineItem) => {
      if (existingOptions && setExistingOptions) {
        setExistingOptions(updateNumberOfExistingPlanOptions({ lineItem, remainingExistingOptions: existingOptions }));
      }

      setLineItems((prev) => {
        return prev.filter((prevLineItem) => {
          return lineItem.site ? prevLineItem.site?.id !== lineItem.site.id : prevLineItem.id !== lineItem.id;
        });
      });

      setChangedLineItems((prev) =>
        prev.filter((item) => (lineItem.site ? item.site?.id !== lineItem.site.id : item.id !== lineItem.id)),
      );
    },
    [existingOptions, setExistingOptions],
  );

  const updateLineItem = useCallback(
    (lineItem: LineItem, selectedPlanOption: PlanSelectOption | undefined) => {
      const formattedPlanOption = formatPlanOptionLabel(selectedPlanOption, currency);

      // Select the date renewal option
      if (formattedPlanOption && (selectedPlanOption?.subscriptionId || lineItem.selectedPlanOption?.subscriptionId)) {
        formattedPlanOption.renewalDateOption = dateOptions?.find(
          (dateOption) => dateOption.value === selectedPlanOption?.subscriptionId,
        );

        if (existingOptions && setExistingOptions && selectedPlanOption) {
          setExistingOptions(
            updateNumberOfExistingPlanOptions({
              selectedPlanOption,
              lineItem,
              remainingExistingOptions: existingOptions,
            }),
          );
        }
      }

      setLineItems((prev) =>
        prev.map((prevLineItem) => {
          if (prevLineItem.id !== lineItem.id || prevLineItem.site?.id !== lineItem.site?.id) {
            return prevLineItem;
          }

          const updatedLineItem = { ...lineItem, selectedPlanOption: formattedPlanOption };

          trackChangedLineItem(updatedLineItem);

          return updatedLineItem;
        }),
      );
    },
    [currency, dateOptions, existingOptions, setExistingOptions, trackChangedLineItem],
  );

  const setItems = useCallback(
    (items: Array<LineItem>) => {
      setLineItems(items);
      setChangedLineItems(
        items.filter((item) => {
          return getChangedLineItems(item, initialItems);
        }),
      );
    },
    [initialItems],
  );

  useEffect(() => {
    if (!planOptions?.length) return;

    setLineItems((prev) => {
      const updatedItems = prev.map((item) => {
        const matchingOption = planOptions.find(
          (option) =>
            option.price?.metadata.badge_name === item.selectedPlanOption?.price?.metadata.badge_name &&
            option.price?.currency === currency &&
            option.price.interval === billingCycle,
        );

        const newSelectedPlanOption = item.selectedPlanOption?.subscriptionId
          ? item.selectedPlanOption
          : matchingOption ?? item.selectedPlanOption;

        return {
          ...item,
          selectedPlanOption: newSelectedPlanOption,
        };
      });

      setChangedLineItems(
        updatedItems.filter((item) => {
          return getChangedLineItems(item, initialItems);
        }),
      );

      // Only update if something actually changed
      const hasChanges = updatedItems.some(
        (item, index) => item.selectedPlanOption !== prev[index]?.selectedPlanOption,
      );

      return hasChanges ? updatedItems : prev;
    });
  }, [planOptions, currency, billingCycle, initialItems]);

  // Check if there are any changes from the initial state
  const hasChanges = useMemo(() => {
    if (lineItems.length !== initialItems.length) return true;

    return lineItems.some((item, index) => {
      const initialItem = initialItems[index];
      return item.selectedPlanOption?.value !== initialItem.selectedPlanOption?.value;
    });
  }, [lineItems, initialItems]);

  const hasDowngrades = useMemo(() => {
    return lineItems.some((item) => {
      // Check if this is a NEW change (different from initial state)
      const initialItem = initialItems.find((initial) => initial.id === item.id);
      const hasChanged = item.selectedPlanOption?.value !== initialItem?.selectedPlanOption?.value;

      if (!hasChanged) return false;

      if (!initialItem?.id || isTrial(initialItem.licenceType)) return false;

      const currentPlanType = initialItem.licenceType;
      const selectedPlanType = item.selectedPlanOption?.price?.metadata.type;

      if (!currentPlanType || !selectedPlanType) return false;

      return hasAnyDowngrade(currentPlanType, selectedPlanType);
    });
  }, [lineItems, initialItems]);

  // Check if we need payment (upgrades from CURRENT plan or new licenses)
  const needsPayment = useMemo(() => {
    const hasNewLicenses = lineItems.some((item) => !item.id);

    // Check if any license is being upgraded from its CURRENT plan (checks both VMS and CMS)
    const hasActualUpgrades = lineItems.some((item) => {
      const initialItem = initialItems.find((initial) => initial.id === item.id);
      const hasChanged = item.selectedPlanOption?.value !== initialItem?.selectedPlanOption?.value;

      if (!hasChanged) return false;

      if (!initialItem?.id || isTrial(initialItem.licenceType)) return false;

      const currentPlanType = initialItem.licenceType;
      const selectedPlanType = item.selectedPlanOption?.price?.metadata.type;

      if (!currentPlanType || !selectedPlanType) return false;

      return hasAnyUpgrade(currentPlanType, selectedPlanType);
    });

    return hasActualUpgrades || hasNewLicenses;
  }, [lineItems, initialItems]);

  return {
    lineItems,
    changedLineItems,
    addLineItem,
    removeLineItem,
    updateLineItem,
    setItems,
    hasDowngrades,
    hasChanges,
    needsPayment,
  };
}
